import { Inject, Injectable } from '@angular/core';
import {
  DwFieldType,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQuery,
  DwQueryFilter,
  DwQueryFilterConverterService,
  DwQueryFilterGroup,
  DwQueryHelper,
  deepCopy
} from '@devwareapps/devware-cap';
import { combineLatest, Observable, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { DwChartColors } from '../../../z-devware/visualizations/models/dw-chart-colors.model';
import { DwChartPreferences, DwChartTypes } from '../../../z-devware/visualizations/models/dw-chart-preferences.model';
import {
  ReportAggregateMeasure,
  ReportDashboardGlobalFilterDefinition,
  ReportSummaryDefinition,
  ReportWidgetDefinition
} from '../models/report-dashboard-definition.model';
import {
  ReportDrillDownKeyResult,
  ReportSummaryResult,
  ReportWidgetResult
} from '../models/report-dashboard-runtime.model';

const DEFAULT_CHART_TOP = 15;
const DEFAULT_GRID_TOP = 100;

@Injectable({ providedIn: 'root' })
export class ReportDashboardRepositoryService {
  constructor(
    @Inject(DwOrmDataServiceToken) private ormDataService: DwOrmDataService,
    @Inject(DwMetaDataServiceToken) private metaDataService: DwMetaDataService,
    private queryFilterConverter: DwQueryFilterConverterService
  ) { }

  loadSummaries(
    definitions: ReportSummaryDefinition[],
    filters: DwQueryFilterGroup,
    globalFilters?: ReportDashboardGlobalFilterDefinition[],
    resolvedContext?: any
  ): Observable<ReportSummaryResult[]> {
    if (!definitions?.length) {
      return of([]);
    }

    return combineLatest(definitions.map(definition =>
      this.loadSummary(definition, filters, globalFilters, resolvedContext)
    )).pipe(take(1));
  }

  loadWidgets(
    definitions: ReportWidgetDefinition[],
    filters: DwQueryFilterGroup,
    globalFilters?: ReportDashboardGlobalFilterDefinition[],
    resolvedContext?: any
  ): Observable<ReportWidgetResult[]> {
    const chartDefinitions = (definitions || []).filter(widget => widget.type === 'chart' && !!widget.request);
    if (!chartDefinitions.length) {
      return of([]);
    }

    return combineLatest(chartDefinitions.map(definition =>
      this.loadWidget(definition, filters, globalFilters, resolvedContext)
    )).pipe(take(1));
  }

  buildDrillDownFilters(result: ReportWidgetResult, event: { categoryValue?: any; series?: { dataField?: string } }): any {
    const filters: any = {};

    const categoryField = this.getFieldName(result.definition.request.categoryField, true);
    filters[categoryField] = event.categoryValue;

    const selectedMeasure = (result.definition.measures || [])
      .find(measure => measure.alias === event.series?.dataField);
    if (selectedMeasure?.field) {
      filters[selectedMeasure.field] = { greaterThan: 0 };
    }

    return filters;
  }

  resolveDrillDownKeys(
    sourceQuery: DwQuery,
    filters: any,
    sourceKeyField: string,
    maxKeys = 1000
  ): Observable<ReportDrillDownKeyResult> {
    if (!sourceQuery?.RootTable || !sourceKeyField) {
      return of({ keys: [], tooMany: false });
    }

    const queryHelper = new DwQueryHelper();
    let keyQuery = Object.assign(new DwQuery(sourceQuery.RootTable), deepCopy(sourceQuery));

    queryHelper.clearAllFields(keyQuery);
    // Retain the source filter so the key population exactly matches the KPI or chart query.
    keyQuery.Order = [];
    keyQuery.FieldSettings.AggregateQuery = false;
    keyQuery.FieldSettings.AggregateQueryWithRollup = false;
    keyQuery.FieldSettings.IncludeRootItemFields = false;
    keyQuery.FieldSettings.LoadAllLookupDisplayFields = false;
    keyQuery.FieldSettings.DistinctRowsOnly = true;
    keyQuery.AddField(sourceKeyField);
    keyQuery.SetupPaging(1, maxKeys + 1, false, maxKeys + 1);
    keyQuery.PagingInfo.Distinct = true;
    keyQuery = queryHelper.convertExistingQuery(keyQuery, filters);

    const keyFieldName = this.getFieldName(sourceKeyField, false);
    return this.ormDataService.executeQuery(keyQuery).pipe(map(rows => {
      const keys = (rows || [])
        .map(row => row?.[keyFieldName])
        .filter(key => key !== null && key !== undefined)
        .filter((key, index, values) => values.indexOf(key) === index);

      return {
        keys: keys.slice(0, maxKeys),
        tooMany: keys.length > maxKeys
      };
    }));
  }

  buildTargetKeyFilters(targetKeyField: string, keys: any[]): any {
    const queryHelper = new DwQueryHelper();
    const keyFilter = new DwQueryFilterGroup();
    keyFilter.AddFilterAnd(queryHelper.filters.InList(targetKeyField, keys));
    return { groupDashboardDrillDownKeys: keyFilter };
  }

  private loadSummary(
    definition: ReportSummaryDefinition,
    filters: DwQueryFilterGroup,
    globalFilters?: ReportDashboardGlobalFilterDefinition[],
    resolvedContext?: any
  ): Observable<ReportSummaryResult> {
    return this.buildAggregateQuery(
      definition.dataSourceItemName || 'roCheckrideFactBase',
      filters,
      definition.staticFilter,
      globalFilters,
      resolvedContext
    )
      .pipe(mergeMap(query => {
        definition.measures.forEach(measure => this.addMeasure(query, measure));
        return this.ormDataService.executeQuery(query)
          .pipe(map(data => ({ key: definition.key, row: data?.[0] || {}, query })));
      }));
  }

  private loadWidget(
    definition: ReportWidgetDefinition,
    filters: DwQueryFilterGroup,
    globalFilters?: ReportDashboardGlobalFilterDefinition[],
    resolvedContext?: any
  ): Observable<ReportWidgetResult> {
    const itemName = definition.request?.queryRequest?.item || 'roCheckrideFactBase';
    return this.buildAggregateQuery(itemName, filters, definition.staticFilter, globalFilters, resolvedContext)
      .pipe(mergeMap(query => {
        const categoryField = this.getFieldName(definition.request.categoryField, true);
        const queryHelper = new DwQueryHelper();

        query.AddField(categoryField);
        (definition.request.additionalGroupingFields || []).forEach(field => query.AddField(this.getFieldName(field, true)));

        if (!definition.request.includeBlank) {
          query.AddFilterAnd(queryHelper.filters.IsNotBlank(categoryField));
        }

        (definition.measures || []).forEach(measure => this.addMeasure(query, measure));
        this.applyWidgetQueryLimit(query, definition);

        return this.ormDataService.executeQuery(query)
          .pipe(map(data => this.createWidgetResult(definition, data || [], query)));
      }));
  }

  private buildAggregateQuery(
    itemName: string,
    filters?: DwQueryFilterGroup,
    staticFilter?: DwQueryFilterGroup,
    globalFilters?: ReportDashboardGlobalFilterDefinition[],
    resolvedContext?: any
  ): Observable<DwQuery> {
    return this.metaDataService.getSavedQueryMetaData(itemName, null, null)
      .pipe(map(result => {
        let query = Object.assign(new DwQuery(result.DataQuery.RootTable), deepCopy(result.DataQuery));
        const queryHelper = new DwQueryHelper();

        query.FieldSettings.AggregateQuery = true;
        query.FieldSettings.LoadAllLookupDisplayFields = true;
        query.FieldSettings.IncludeRootItemFields = false;
        queryHelper.clearAllFields(query);
        query.Order = [];

        const globalFilter = this.convertGlobalFilter(itemName, globalFilters, resolvedContext);
        query = queryHelper.convertExistingQuery(query, this.mergeFilters(globalFilter, filters, staticFilter));
        return query;
      }));
  }

  private convertGlobalFilter(
    itemName: string,
    globalFilters?: ReportDashboardGlobalFilterDefinition[],
    resolvedContext?: any
  ): DwQueryFilter {
    const definition = (globalFilters || []).find(filter =>
      filter.dataSourceItemName?.toLowerCase() === itemName?.toLowerCase()
    );
    if (!definition?.filterExpression) {
      return null;
    }

    const filter = this.queryFilterConverter.convertExpressionToFilter(
      definition.filterExpression,
      resolvedContext || {}
    );
    if (!filter || ((filter as DwQueryFilterGroup).Filters && !(filter as DwQueryFilterGroup).Filters.length)) {
      throw new Error(`Global filter for ${itemName} could not be converted to a query filter.`);
    }
    return filter;
  }

  private mergeFilters(...filters: DwQueryFilter[]): DwQueryFilterGroup {
    const merged = new DwQueryFilterGroup();
    filters
      .filter(filter => !!filter && (!(filter as DwQueryFilterGroup).Filters
        || !!(filter as DwQueryFilterGroup).Filters.length))
      .forEach(filter => merged.AddFilterAnd(deepCopy(filter)));
    return merged;
  }

  private addMeasure(query: DwQuery, measure: ReportAggregateMeasure): void {
    query.AddField({
      FieldType: DwFieldType.Aggregate,
      AggregateFunction: measure.aggregateFunction,
      FieldAlias: measure.alias,
      FullFieldName: measure.field
    });
  }

  private createWidgetResult(definition: ReportWidgetDefinition, sourceData: any[], query: DwQuery): ReportWidgetResult {
    const data = sourceData.map(row => ({ ...row }));
    const chartType = toLegacyChartType(definition.request.chartType);
    const isRadialChart = chartType === DwChartTypes.pie
      || chartType === DwChartTypes.doughnut
      || chartType === DwChartTypes.polarArea;

    (definition.computedMeasures || []).forEach(computed => {
      data.forEach(row => {
        const numerator = Number(row[computed.numeratorField]) || 0;
        const denominator = Number(row[computed.denominatorField]) || 0;
        const value = denominator > 0 ? (numerator / denominator) * (computed.multiplier || 1) : 0;
        row[computed.alias] = Number(value.toFixed(computed.decimals ?? 0));
      });
    });

    if (definition.categoryOrder?.length) {
      const categoryField = this.getFieldName(definition.request.categoryField, false);
      data.sort((a, b) => definition.categoryOrder.indexOf(a[categoryField]) - definition.categoryOrder.indexOf(b[categoryField]));
    } else if (definition.sortField) {
      data.sort((a, b) => {
        const direction = definition.sortDescending === false ? 1 : -1;
        return (Number(a[definition.sortField]) - Number(b[definition.sortField])) * direction;
      });
    }

    const chartTop = this.getChartTop(definition);
    const gridTop = this.getGridTop(definition);
    const limitedData = chartTop ? data.slice(0, chartTop) : data;
    const gridData = gridTop ? data.slice(0, gridTop) : data;
    const categoryField = this.getFieldName(definition.request.categoryField, false);
    const series = [
      ...(definition.measures || []).filter(measure =>
        measure.showInChart !== false && (!definition.computedMeasures?.length || measure.showInChart === true)
      ).map((measure, index) => {
        const color = measure.color || DwChartColors[index % DwChartColors.length];
        return {
          chartType,
          dataField: measure.alias,
          legendDisplay: measure.legendDisplay,
          valueSuffix: measure.valueSuffix,
          axis: measure.axis,
          backgroundColor: color,
          borderColor: getReportSeriesBorderColor(chartType, color),
          alternateColors: definition.alternateColors ?? isRadialChart,
          alternateColorStart: (definition.alternateColorStart || 0) + index
        };
      }),
      ...(definition.computedMeasures || []).filter(measure => measure.showInChart !== false).map((measure, index) => {
        const color = measure.color
          || DwChartColors[(index + (definition.measures?.length || 0)) % DwChartColors.length];
        return {
          chartType,
          dataField: measure.alias,
          legendDisplay: measure.legendDisplay,
          valueSuffix: measure.valueSuffix,
          axis: measure.axis,
          backgroundColor: color,
          borderColor: getReportSeriesBorderColor(chartType, color),
          alternateColors: definition.alternateColors ?? isRadialChart,
          alternateColorStart: (definition.alternateColorStart || 0) + index
        };
      })
    ];

    const chartPreferences: DwChartPreferences = {
      chartType,
      categoryField: definition.request.categoryDisplayField || categoryField,
      categoryValueField: categoryField,
      categoryDisplay: definition.request.categoryDisplayOverride,
      series,
      hideLegend: definition.request.hideLegend,
      showContainerTitle: true,
      containerTitle: definition.title,
      containerDescription: definition.description,
      showValues: definition.showValues,
      valueAxisMax: definition.valueAxisMax,
      valueLabelPosition: definition.valueLabelPosition,
      gridPreferences: {
        showGrid: true,
        showTotalsRow: definition.showGridTotals !== false,
        categoryColumnWidth: 220,
        useChartDataForGrid: true,
        gridConfigKeyPart: `${definition.key}-chart-grid`
      }
    };

    return { definition, data: limitedData, gridData, query, chartPreferences };
  }

  private applyWidgetQueryLimit(query: DwQuery, definition: ReportWidgetDefinition): void {
    const queryTop = this.getQueryTop(definition);
    if (!queryTop) {
      return;
    }

    const aggregateSortField = (definition.measures || [])
      .find(measure => measure.alias === definition.sortField)?.alias
      || (definition.measures || []).find(measure => measure.showInChart !== false)?.alias
      || definition.measures?.[0]?.alias;

    if (aggregateSortField) {
      query.OrderBy(aggregateSortField, definition.sortDescending === false);
    }

    query.SetupPaging(1, queryTop, false, queryTop);
  }

  private getQueryTop(definition: ReportWidgetDefinition): number | null {
    const chartTop = this.getChartTop(definition);
    const gridTop = this.getGridTop(definition);

    if (!chartTop || !gridTop) {
      return null;
    }

    return Math.max(chartTop, gridTop);
  }

  private getChartTop(definition: ReportWidgetDefinition): number | null {
    const configuredTop = definition.maxItems ?? definition.request?.maxItems;
    if (configuredTop === 0) {
      return null;
    }

    const chartTop = configuredTop ?? DEFAULT_CHART_TOP;
    return Number.isFinite(Number(chartTop)) && Number(chartTop) > 0
      ? Math.floor(Number(chartTop))
      : DEFAULT_CHART_TOP;
  }

  private getGridTop(definition: ReportWidgetDefinition): number | null {
    const configuredTop = definition.gridMaxItems;
    if (configuredTop === 0) {
      return null;
    }

    const gridTop = configuredTop ?? DEFAULT_GRID_TOP;
    return Number.isFinite(Number(gridTop)) && Number(gridTop) > 0
      ? Math.floor(Number(gridTop))
      : DEFAULT_GRID_TOP;
  }

  private getFieldName(field: any, includeItemName: boolean): string {
    if (typeof field === 'string') {
      if (includeItemName) {
        return field;
      }
      const parts = field.split('.');
      return parts.length === 2 ? parts[1] : field;
    }

    return this.getFieldName(field?.field, includeItemName);
  }
}

export function getReportSeriesBorderColor(chartType: DwChartTypes, color: string): string {
  return chartType === DwChartTypes.line || chartType === DwChartTypes.radar
    ? color
    : 'white';
}

/**
 * ChartDataRequest comes from the current CAP package, where chart types are
 * string enum values. Pilexos' shared chart tile predates that change and uses
 * the equivalent numeric enum, so normalize at the dashboard adapter boundary.
 */
export function toLegacyChartType(chartType: DwChartTypes | string): DwChartTypes {
  if (typeof chartType === 'number' && DwChartTypes[chartType] !== undefined) {
    return chartType;
  }

  const legacyChartType = (DwChartTypes as any)[chartType];
  if (typeof legacyChartType === 'number') {
    return legacyChartType as DwChartTypes;
  }

  throw new Error(`Unsupported report chart type: ${chartType}`);
}
